"use client";

import React from "react";
import { getPaginationItems } from "@/lib/utils/pagination";

/**
 * 分页组件
 * 用于显示分页导航，支持页码跳转、上一页/下一页操作
 * 包含智能分页逻辑，当页码过多时自动显示省略号
 */

/**
 * 分页组件属性接口
 */
interface PaginationProps {
  /** 当前页码 */
  currentPage: number;
  /** 总页数 */
  totalPages: number;
  /** 页码变化回调函数 */
  onPageChange: (page: number) => void;
  /** 是否处于加载状态，加载时禁用所有按钮 */
  loading?: boolean;
}

/**
 * 分页组件主函数
 * 渲染分页导航UI，包括上一页按钮、页码按钮、省略号和下一页按钮
 */
export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  loading = false,
}: PaginationProps) {
  // 如果总页数小于等于1，不显示分页组件
  if (totalPages <= 1) {
    return null;
  }

  // 获取分页项数组
  const paginationItems = getPaginationItems(currentPage, totalPages);

  return (
    <div className="flex justify-center mt-[72px]">
      {/* 分页容器：圆角背景，浅米色 */}
      <div className="flex items-center gap-[20px] px-[28px] py-[10px]">
        {/* 上一页按钮 */}
        <button
          onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
          disabled={currentPage === 1 || loading}
          className="w-[32px] h-[32px] flex items-center justify-center rounded-full border border-[#E5E5E4] text-[#2A2A29] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <img
            src="/assets/image/blog/prev-arrow-black.svg"
            alt="Previous page"
            className="w-[12px] h-[12px]"
          />
        </button>

        {/* 渲染分页项：页码按钮或省略号 */}
        {paginationItems.map((item, index) => {
          // 如果是省略号，显示三个小圆点
          if (item.type === "ellipsis") {
            return (
              <span
                key={`ellipsis-${index}`}
                className="flex h-[32px] items-center justify-center gap-[4px] select-none"
              >
                <span className="w-[4px] h-[4px] rounded-full bg-[rgba(0,0,0,0.25)]"></span>
                <span className="w-[4px] h-[4px] rounded-full bg-[rgba(0,0,0,0.25)]"></span>
                <span className="w-[4px] h-[4px] rounded-full bg-[rgba(0,0,0,0.25)]"></span>
              </span>
            );
          }

          // 页码按钮
          return (
            <button
              key={item.page}
              onClick={() =>
                item.page !== currentPage && onPageChange(item.page)
              }
              disabled={loading}
              className={`min-w-[32px] h-[32px] px-[8px] text-[14px] flex items-center justify-center rounded-full transition-colors ${
                item.page === currentPage
                  ? "text-[#FF596C] font-[700] border border-[#FF596C]" // 当前页：红色文字和边框
                  : "text-[rgba(0,0,0,0.85)] hover:border hover:border-[#FF596C]" // 其他页：灰色文字，悬停时显示红色边框
              }`}
            >
              {item.page}
            </button>
          );
        })}

        {/* 下一页按钮 */}
        <button
          onClick={() =>
            currentPage < totalPages && onPageChange(currentPage + 1)
          }
          disabled={currentPage === totalPages || loading}
          className="w-[32px] h-[32px] flex items-center justify-center rounded-full border border-[#E5E5E4] text-[#2A2A29] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <img
            src="/assets/image/blog/next-arrow-black.svg"
            alt="Next page"
            className="w-[12px] h-[12px]"
          />
        </button>
      </div>
    </div>
  );
}
